// BACS history — Wave D.
//
// List of BACS payment runs (draft / submitted / voided) with status filter,
// date range, pagination, and a per-row action set: open, download Standard
// 18 (if available).

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Eye } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { FilterBar } from '../../components/ui/FilterBar/FilterBar';
import { Table, type Column, type RowAction } from '../../components/ui/Table/Table';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import { useBacsList } from './hooks/useBacs';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useToast } from '../../hooks/useToast';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import type { BacsRunStatus, BacsRunSummary } from '../../types/api';
import styles from './BACSHistoryPage.module.css';

const STATUS_MAP: Record<BacsRunStatus, BadgeVariant> = {
  draft: 'muted',
  submitted: 'success',
  voided: 'danger',
};

const STATUS_LABELS: Record<BacsRunStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  voided: 'Voided',
};

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

async function downloadRun(encId: string, reference: string, onError: () => void) {
  try {
    const res = await apiClient.raw.get<Blob>(endpoints.bacs.exportFile(encId), {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reference}.txt`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    onError();
  }
}

export function BACSHistoryPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const status = (params.get('status') ?? '') as '' | BacsRunStatus;
  const dateFrom = params.get('date_from') ?? '';
  const dateTo = params.get('date_to') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const list = useBacsList({
    page,
    status: status || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const runs = list.data?.runs ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const [, setDownloading] = useState<string | null>(null);

  const columns: Column<BacsRunSummary>[] = [
    {
      key: 'reference',
      header: 'Reference',
      render: (_v, row) => (
        <button
          type="button"
          className={styles.refLink}
          onClick={() => navigate(`/collections/bacs/${encodeURIComponent(row.enc_id)}`)}
        >
          {row.reference}
        </button>
      ),
    },
    {
      key: 'payment_date',
      header: 'Payment date',
      render: (_v, row) => formatDate(row.payment_date),
    },
    { key: 'total_records', header: 'Records', align: 'right' },
    {
      key: 'total_amount',
      header: 'Total',
      align: 'right',
      render: (_v, row) => formatMoney(row.total_amount),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => (
        <StatusBadge
          status={row.status}
          statusMap={STATUS_MAP}
          formatLabel={(s) => STATUS_LABELS[s] ?? s}
        />
      ),
    },
    { key: 'submitted_at', header: 'Submitted', render: (_v, row) => formatDate(row.submitted_at) },
  ];

  const rowActions = (row: BacsRunSummary): RowAction<BacsRunSummary>[] => {
    const actions: RowAction<BacsRunSummary>[] = [
      {
        label: 'Open',
        icon: Eye,
        onClick: () => navigate(`/collections/bacs/${encodeURIComponent(row.enc_id)}`),
        variant: 'ghost',
      },
    ];
    if (row.status !== 'voided') {
      actions.push({
        label: 'Download',
        icon: Download,
        onClick: () => {
          setDownloading(row.enc_id);
          void downloadRun(row.enc_id, row.reference, () =>
            toast.error('Download failed.'),
          ).then(() => setDownloading(null));
        },
        variant: 'ghost',
      });
    }
    return actions;
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="BACS history"
        subtitle={`${total} run${total === 1 ? '' : 's'}`}
        actions={
          <Button variant="secondary" onClick={() => navigate('/collections/payment-run')}>
            Payment run
          </Button>
        }
      />

      <Card flush>
        <FilterBar>
          <DatePicker
            aria-label="Date from"
            value={dateFrom}
            onChange={(e) => setParam('date_from', e.target.value)}
          />
          <DatePicker
            aria-label="Date to"
            value={dateTo}
            onChange={(e) => setParam('date_to', e.target.value)}
          />
          <Select
            aria-label="Status filter"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'submitted', label: 'Submitted' },
              { value: 'voided', label: 'Voided' },
            ]}
          />
        </FilterBar>

        <Table
          columns={columns}
          data={runs}
          keyField={(row) => row.enc_id}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <h3>No BACS runs yet</h3>
              <p>Generated payment runs will appear here.</p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel="BACS history"
        />

        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? 50, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>
    </div>
  );
}
